/**
 * Apply a published translation overlay onto English master policy fields.
 * Used for instant client-side language switching (no new IndicTrans request).
 *
 * If a non-English language has no ready translation, fall back to English master
 * and languageSelected=en — never claim Hindi (etc.) while showing English copy.
 */
import type {
  ConsentSdkPolicy,
  ConsentSdkTranslatedContent,
} from "./consent-sdk.types.ts";

export function applyTranslationOverlay(
  master: ConsentSdkPolicy,
  language: string,
): ConsentSdkPolicy {
  const lang = (language || "en").toLowerCase();
  // Master content is always English; a non-English defaultLanguage still needs its overlay.
  if (lang === "en") {
    return {
      ...master,
      languageSelected: "en",
      title: master.localization?.masterTitle ?? master.title,
      notice: master.localization?.masterNotice ?? master.notice,
      purposes: master.localization?.masterPurposes ?? master.purposes,
      theme: {
        ...master.theme,
        bannerHeadline:
          master.localization?.masterBannerHeadline ?? master.theme?.bannerHeadline,
        bannerBody: master.localization?.masterBannerBody ?? master.theme?.bannerBody,
      },
    };
  }

  const tx: ConsentSdkTranslatedContent | undefined =
    master.localization?.translations?.[lang];
  if (!tx) {
    // Not ready — do not expose as a fake translated language.
    return {
      ...master,
      languageSelected: "en",
      title: master.localization?.masterTitle ?? master.title,
      notice: master.localization?.masterNotice ?? master.notice,
      purposes: master.localization?.masterPurposes ?? master.purposes,
      theme: {
        ...master.theme,
        bannerHeadline:
          master.localization?.masterBannerHeadline ?? master.theme?.bannerHeadline,
        bannerBody: master.localization?.masterBannerBody ?? master.theme?.bannerBody,
      },
    };
  }

  const masterPurposes = master.localization?.masterPurposes ?? master.purposes ?? [];
  let purposes = masterPurposes;
  if (tx.purposes?.length) {
    const byId = new Map(tx.purposes.map((p) => [p.id, p]));
    purposes = masterPurposes.map((p) => {
      const t = byId.get(p.id);
      if (!t) return p;
      return {
        ...p,
        name: t.name ?? p.name,
        description: t.description ?? p.description,
      };
    });
  }

  const title = tx.title ?? master.localization?.masterTitle ?? master.title;
  const notice = tx.notice ?? master.localization?.masterNotice ?? master.notice;
  // Prefer translated notice/title when dedicated banner fields were not stored.
  // Never fall back to English masterBanner* before the translated notice/title.
  const bannerHeadline =
    tx.bannerHeadline ??
    title ??
    master.localization?.masterBannerHeadline ??
    master.theme?.bannerHeadline;
  const bannerBody =
    tx.bannerBody ??
    notice ??
    master.localization?.masterBannerBody ??
    master.theme?.bannerBody ??
    undefined;

  return {
    ...master,
    languageSelected: lang,
    title,
    notice,
    purposes,
    theme: {
      ...master.theme,
      bannerHeadline: bannerHeadline ?? undefined,
      bannerBody: bannerBody ?? undefined,
    },
  };
}

/**
 * Languages the end user may select: English + codes that have a ready translation
 * object. Never include admin-enabled-but-unready languages.
 */
export function readySelectorLanguages(
  localization:
    | {
        languages?: string[];
        enabledLanguages?: string[];
        translations?: Record<string, unknown>;
        multilingualEnabled?: boolean;
      }
    | undefined
    | null,
): string[] {
  const translations = localization?.translations ?? {};
  const readyFromMap = Object.keys(translations).filter((l) => l && l !== "en");
  // Prefer server `languages` when present (already readiness-filtered).
  const fromServer = (localization?.languages ?? []).filter(Boolean);
  if (fromServer.length) {
    const readySet = new Set(["en", ...readyFromMap.map((l) => l.toLowerCase())]);
    const filtered = fromServer
      .map((l) => l.toLowerCase())
      .filter((l) => l === "en" || readySet.has(l) || Boolean(translations[l]));
    const uniq = [...new Set(filtered.length ? filtered : ["en", ...readyFromMap])];
    return uniq.length ? uniq : ["en"];
  }
  return ["en", ...readyFromMap];
}
